import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/react-hooks';
import * as selectors from 'containers/Campaigns/CreateEdit/store/selectors';
import * as actions from 'containers/Campaigns/CreateEdit/store/actions';
import { DetailsType, SelectionType } from 'containers/Campaigns/CreateEdit/store/reducer';
import * as campaignsActions from 'containers/Campaigns/actions';
import {
  CreateCampaignDocument as createCampaignQuery,
  CreateCampaignMutation as createCampaignResult,
  CreateCampaignMutationVariables as createCampaignVars,
} from 'generated/campaigns-graphql';
import { campaignsClient } from 'apollo';
import {
  clientIdSelector,
  currentBrandSelector,
  isEnterpriseContextSelector,
} from 'store/global/selectors';
import { CAMPAIGNS_TYPE, CONTEXT_ITEM_GROUP, CONTEXT_STORE_GROUP } from 'utils/types';
import { useCampaignsState } from 'providers/campaigns';
import { selectionSelector } from 'components/FilterModal/store/selectors';
import * as filterActions from 'components/FilterModal/store/actions';
import SaveButton from 'components/Buttons/SaveButton';

const CreateCampaignAction: React.FC = () => {
  const { refetchFunctions } = useCampaignsState();
  const dispatch = useDispatch();
  const clientId = useSelector<string>(clientIdSelector);
  const currentBrand = useSelector<string>(currentBrandSelector);
  const isEnterpriseContext = useSelector<boolean>(isEnterpriseContextSelector);

  // Get data from Filter Modal Store
  const selection = useSelector<SelectionType>(selectionSelector);
  const details = useSelector<DetailsType>(selectors.detailsSelector);

  const handleSave = (createCampaign) => {
    createCampaign({
      variables: {
        input: {
          client: clientId,
          ...(isEnterpriseContext && { brand: currentBrand }),
          storeContext: CONTEXT_STORE_GROUP,
          itemContext: CONTEXT_ITEM_GROUP,
          name: details.name,
          storeGroupName: details.storeGroupName,
          itemGroupName: details.itemGroupName,
          objective: details.objective,
          startDate: details.startDate,
          endDate: details.endDate !== '' ? details.endDate : null,
          notes: details.notes !== '' ? details.notes : null,
        },
        filters: {
          accounts: selection.accounts.map((item) => item.id),
          stores: selection.stores.map((item) => item.id),
          states: selection.states.map((item) => item.id),
          items: selection.items.map((item) => item.id),
        },
      },
    });
  };

  const onCreateSuccess = () => {
    dispatch(actions.setCurrentStep(4));
    dispatch(actions.finishCreation());
    dispatch(actions.reset());
    dispatch(filterActions.clearSelections());
  };

  const [createCampaign, { loading, error }] = useMutation<
    createCampaignResult,
    createCampaignVars
  >(createCampaignQuery, {
    client: campaignsClient as any,
    onCompleted: ({ create }) => {
      onCreateSuccess();
      dispatch(campaignsActions.selectCampaign(create.id));
      refetchFunctions.listCampaigns(CAMPAIGNS_TYPE);
    },
    onError: () => {
      dispatch(actions.setSavingError(error.message));
    },
  });

  const isDisabled = () => {
    return (
      details.name === '' ||
      details.storeGroupName === '' ||
      details.itemGroupName === '' ||
      details.objective === null ||
      details.startDate === ''
    );
  };

  return (
    <SaveButton
      isDisabled={isDisabled()}
      loading={loading}
      onClick={() => handleSave(createCampaign)}
    />
  );
};

export default CreateCampaignAction;
